import React, { FC, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { ExpandMore, OpenInNew } from '@mui/icons-material';
import { TERMLEX_DEFINITIONS } from './dictionary.graphql';
import { matchableWord } from './search';

type TermlexDefinition = { identifier: string; term: string; definition: string; source: string; url: string };

const TERMLEX_NAVY = '#000d3f';
const TERMLEX_MINT = '#bdefdf';
const TERMLEX_GREEN = '#00c785';

export const useTermlexDefinitions = (term: string) => {
  const [query, setQuery] = useState(() => matchableWord(term));
  useEffect(() => {
    const timer = setTimeout(() => setQuery(matchableWord(term)), 400);
    return () => clearTimeout(timer);
  }, [term]);

  const { data, loading, error } = useQuery(TERMLEX_DEFINITIONS, { variables: { term: query }, skip: !query });
  const definitions = (data?.termlexDefinitions ?? []).filter((d): d is TermlexDefinition => !!d);
  return { query, definitions, loading, error: !!error, done: !loading && !error && !!data };
};

export type TermlexResult = ReturnType<typeof useTermlexDefinitions>;

export const TermlexDefinitionList: FC<{ result: TermlexResult; onUse?: (d: TermlexDefinition) => void }> = ({ result, onUse }) => {
  const { query, definitions, error, done } = result;
  return (
    <>
      {error && (
        <Typography variant="body2" color="textSecondary">
          Termlex er ikke tilgjengelig akkurat nå.
        </Typography>
      )}
      {done && definitions.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          Ingen definisjoner i Termlex for «{query}».
        </Typography>
      )}
      {done &&
        definitions.map((d) => (
          <Box key={d.identifier} sx={{ py: 1, borderTop: 1, borderColor: 'divider' }}>
            {d.term.toLowerCase() !== query.toLowerCase() && (
              <Typography variant="caption" color="textSecondary" component="div">
                {d.term}
              </Typography>
            )}
            <Typography variant="body2" dangerouslySetInnerHTML={{ __html: d.definition }} />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
              <Typography variant="caption" color="textSecondary" sx={{ flex: 1 }}>
                {d.source}
              </Typography>
              <Link
                href={d.url}
                target="_blank"
                rel="noopener"
                variant="caption"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: TERMLEX_NAVY }}>
                Se i Termlex <OpenInNew fontSize="inherit" />
              </Link>
              {onUse && (
                <Button size="small" variant="outlined" onClick={() => onUse(d)} sx={{ color: TERMLEX_NAVY, borderColor: TERMLEX_NAVY }}>
                  Bruk
                </Button>
              )}
            </Stack>
          </Box>
        ))}
    </>
  );
};

export const TermlexDefinitionPageList: FC<{ result: TermlexResult }> = ({ result }) => {
  const { query, definitions, error, done, loading } = result;
  if (loading) return <CircularProgress size={24} sx={{ m: 2 }} />;
  if (error || (done && definitions.length === 0)) return <TermlexDefinitionList result={result} />;
  return (
    <List disablePadding>
      {definitions.map((d, i) => (
        <ListItem
          key={d.identifier}
          divider={i < definitions.length - 1}
          disableGutters
          secondaryAction={
            <Tooltip title="Se i Termlex">
              <IconButton edge="end" href={d.url} target="_blank" rel="noopener" aria-label={`Se «${d.term}» i Termlex`}>
                <OpenInNew fontSize="small" />
              </IconButton>
            </Tooltip>
          }>
          <ListItemText
            primary={<span dangerouslySetInnerHTML={{ __html: d.definition }} />}
            secondary={d.term.toLowerCase() !== query.toLowerCase() ? `${d.term}, ${d.source}` : d.source}
            sx={{ pr: 4, pl: 2, borderLeft: 3, borderColor: TERMLEX_GREEN }}
          />
        </ListItem>
      ))}
    </List>
  );
};

export const TermlexDefinitions: FC<{ term: string; hasDefinition: boolean; onUse: (html: string) => void }> = ({ term, hasDefinition, onUse }) => {
  const result = useTermlexDefinitions(term);
  const [expanded, setExpanded] = useState(false);
  const [instant, setInstant] = useState(false);

  if (!result.query) return null;

  const use = (d: TermlexDefinition) => {
    if (hasDefinition && !window.confirm('Erstatte gjeldende definisjon?')) return;
    onUse(`<p>${d.definition}</p>`);
    setInstant(true);
    setExpanded(false);
  };

  const count = result.done ? ` (${result.definitions.length})` : '';
  return (
    <Accordion
      expanded={expanded}
      onChange={(_e, open) => {
        setInstant(false);
        setExpanded(open);
      }}
      slotProps={instant ? { transition: { timeout: 0 } } : undefined}
      disableGutters
      elevation={0}
      sx={{
        '&::before': { display: 'none' },
        border: 1,
        borderColor: TERMLEX_MINT,
        borderLeft: `4px solid ${TERMLEX_GREEN}`,
        borderRadius: 1,
        overflow: 'hidden',
      }}>
      <AccordionSummary expandIcon={<ExpandMore fontSize="small" sx={{ color: TERMLEX_NAVY }} />} sx={{ minHeight: 44 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" sx={{ color: TERMLEX_NAVY, fontWeight: 500 }}>
            Definisjoner i Termlex{count}
          </Typography>
          {result.loading && <CircularProgress size={14} sx={{ color: TERMLEX_NAVY }} />}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <TermlexDefinitionList result={result} onUse={use} />
      </AccordionDetails>
    </Accordion>
  );
};
